import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetDirectGasEmissionsActivityList } from '@shared/api/hooks/ccf/activities/direct-gas-emissions';
import { CompanyReportsActivitiesDirectGasEmissionsListSourcesItem } from '@shared/api/types';
import Text from '@shared/components/content/text';
import TextList from '@shared/components/content/text-list';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCCF } from '../../ccf-context';
import DirectGasEditForm from '../../components/scope-1/direct-gas-edit-form';
import DirectGasTable from '../../components/scope-1/direct-gas-table';
import { reportSlugs } from '../../slugs';
import { useCategory } from '../../utils';
import EmptyCategoryPage from '../ccf-category-intro-page';

export default function DirectGasEmissionsPage() {
  const { uuid: reportUuid } = useCCF();
  const category = useCategory(reportSlugs.directGasEmissions);
  const {
    showSimpleDialog: showDirectGasDialog,
    SimpleDialogComponent: DirectGasDialog,
    closeSimpleDialog: closeDirectGasDialog,
  } = useSimpleDialog();

  const { page, perPage, period, site, sort, supplier } =
    useDataTableQueryState({
      filters: ['supplier', 'period', 'site'],
    });
  const [sources] = useQueryState('source', parseAsArrayOf(parseAsString));
  const [gases] = useQueryState('gas', parseAsArrayOf(parseAsString));
  const uuid = useQueryState('uuid', parseAsArrayOf(parseAsString));

  const {
    data,
    isPreviousData,
    isFetchedAfterMount,
    isLoading,
    isError,
    isFetching,
    status,
  } = useGetDirectGasEmissionsActivityList(reportUuid!, {
    page: page,
    page_size: perPage,
    suppliers: supplier || undefined,
    time_periods: period || undefined,
    sites: site || undefined,
    gases: gases || undefined,
    sources: sources?.map(
      (source) =>
        source as CompanyReportsActivitiesDirectGasEmissionsListSourcesItem
    ),
    search: uuid || undefined,
    sort: formatMultipleSortingForEndpoint(sort! || []),
  });

  const Description = (
    <>
      <Text>
        Direct gas emissions come from the{' '}
        <span className="font-semibold">use and leakage of specific gases</span>{' '}
        during business operations. These emissions are categorised as Scope 1
        because they are directly produced by your business activities.
      </Text>
      <Text className="mt-3">Examples include:</Text>
      <TextList>
        <li>
          <span className="font-semibold">Leakage of refrigerants</span> like
          HFCs from cooling systems (e.g. air conditioners, chillers, freezers).
        </li>
        <li>
          <span className="font-semibold">Leakage of gases</span> like CO₂ and
          N₂O from fire extinguishers, or similar equipment.
        </li>
        <li>
          <span className="font-semibold">Methane or nitrous oxide leaks</span>{' '}
          from waste management or wastewater treatment.
        </li>
      </TextList>
    </>
  );

  return (
    <Page
      title="Scope 1 - Direct gas emissions"
      name="Scope 1 - Direct gas emissions"
      page="subpage"
    >
      {category.hasActivityData ? (
        <DirectGasTable
          data={data!}
          status={status}
          isLoading={isLoading}
          isFetching={isFetching}
          isPreviousData={isPreviousData}
          isFetchedAfterMount={isFetchedAfterMount}
        />
      ) : (
        <EmptyCategoryPage
          categorySlug="direct-gas-emissions"
          description={Description}
          primaryActionProps={{
            onClick: showDirectGasDialog,
          }}
        />
      )}
      {DirectGasDialog({
        header: 'Add direct gas data',
        content: <DirectGasEditForm closeDialog={closeDirectGasDialog} />,
      })}
    </Page>
  );
}
